import axios from "axios";


// 設定API基本URL
const BASE_URL = "/";

// Token 管理
// let authToken = "";

// 創建一個 axios 實例
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  // withCredentials: false, // 允許跨域請求攜帶 cookie
  headers: {
    "Content-Type": "application/json",
    // Authorization: `Bearer ${authToken}`,
    accept: "text/plain",
  },
});

// // 添加請求攔截器
apiClient.interceptors.request.use(
  (config) => {
    // 確保每個請求都有最新的 token
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 添加響應攔截器
apiClient.interceptors.response.use(
  (response) => {
    // 檢查響應數據是否包含必要的字段
    if (!response.data) {
      throw new Error("響應數據為空");
    }
    return response;
  },
  async (error) => {
    // 處理錯誤響應
    if (error.response) {
      // 服務器返回了錯誤狀態碼
      let errorData = error.response.data;
      let errorMessage = `請求失敗 (${error.response.status})`;

      // 如果是 blob 響應但發生錯誤，嘗試將 blob 轉換為文字來獲取錯誤訊息
      if (errorData instanceof Blob) {
        try {
          const text = await errorData.text();
          errorData = JSON.parse(text);
        } catch (parseError) {
          // 如果無法解析，就使用原始文字
          try {
            errorData = await errorData.text();
          } catch (textError) {
            errorData = null;
          }
        }
      }

      // 嘗試從不同的欄位中獲取錯誤訊息
      if (errorData?.detail) {
        errorMessage += `: ${errorData.detail}`;
      } else if (errorData?.message) {
        errorMessage += `: ${errorData.message}`;
      } else if (errorData?.Message) {
        errorMessage += `: ${errorData.Message}`;
      } else if (errorData?.title) {
        errorMessage += `: ${errorData.title}`;
      } else if (typeof errorData === "string") {
        errorMessage += `: ${errorData}`;
      } else {
        errorMessage += `: 未知錯誤`;
      }

      throw new Error(errorMessage);
    } else if (error.request) {
      // 請求已發出但沒有收到響應
      throw new Error("網絡錯誤：無法連接到服務器");
    } else {
      // 請求配置出錯
      throw new Error(`請求配置錯誤: ${error.message}`);
    }
  }
);

// Helper function to make TOCP API requests
async function tocpRequest(method, url, data = null, params = null, responseType = "json") {
  // The apiClient interceptor will handle errors
  const config = {
    method,
    url,
    responseType,
  };
  if (data) {
    config.data = data;
  }
  if (params) {
    config.params = params;
  }
  const response = await apiClient(config);
  return response.data; // Axios handles parsing based on responseType, data is in response.data
}

// 主資料庫 API
export const account = {
  // 取得所有使用者列表
  getList: async () => {
    try {
      const response = await apiClient.get("/api/Account/GetList");
      if (!response.data) {
        throw new Error("獲取使用者列表失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取使用者列表失敗:", error);
      throw error;
    }
  },

  // 取得當前登入使用者資訊
  getMe: async () => {
    try {
      const response = await apiClient.get("/api/Account/Me");
      if (!response.data) {
        throw new Error("獲取當前使用者資訊失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取當前使用者資訊失敗:", error);
      throw error;
    }
  },
};

export const mainDatabase = {
  // 取得資料庫列表
  index: async (limit, page) => {
    try {
      const response = await apiClient.get("/api/TOCP/MainDatabase/Index", {
        params: {
          Limit: limit,
          Page: page,
        },
      });
      if (!response.data) {
        throw new Error("獲取資料庫列表失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取資料庫列表失敗:", error);
      throw error;
    }
  },

  // 取得指定使用者的資料庫列表
  getByAccountId: async (accountId) => {
    try {
      const response = await apiClient.get("/api/TOCP/MainDatabase/Get", {
        params: {
          AccountId: accountId,
        },
      });
      if (!response.data) {
        throw new Error("獲取使用者資料庫列表失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取使用者資料庫列表失敗:", error);
      throw error;
    }
  },

  // 取得資料庫
  get: async (mainDatabaseId, accountId) => {
    try {
      const response = await apiClient.get("/api/TOCP/MainDatabase/Get", {
        params: {
          MainDatabaseId: mainDatabaseId,
          AccountId: accountId,
        },
      });
      if (!response.data) {
        throw new Error("獲取資料庫詳情失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取資料庫詳情失敗:", error);
      throw error;
    }
  },

  // 創建資料庫
  create: async (params) => {
    try {
      if (!params.MainDatabaseName) {
        throw new Error("缺少資料庫名稱");
      }

      const response = await apiClient.post("/api/TOCP/MainDatabase/Create", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建資料庫失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建資料庫失敗:", error);
      throw error;
    }
  },

  // 更新資料庫
  update: async (params) => {
    try {
      if (!params.MainDatabaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!params.MainDatabaseName) {
        throw new Error("缺少資料庫名稱");
      }

      const response = await apiClient.put("/api/TOCP/MainDatabase/Update", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新資料庫失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新資料庫失敗:", error);
      throw error;
    }
  },

  // 刪除資料庫
  delete: async (mainDatabaseId) => {
    try {
      if (!mainDatabaseId) {
        throw new Error("缺少資料庫ID");
      }

      const response = await apiClient.delete("/api/TOCP/MainDatabase/Delete", {
        data: {
          MainDatabaseId: mainDatabaseId,
        },
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });

      return response.data;
    } catch (error) {
      console.error("刪除資料庫失敗:", error);
      throw error;
    }
  },
};

// 列車 API
export const train = {
  // 獲取列車列表
  get: async (trainId, databaseId) => {
    try {
      const response = await apiClient.get("/api/TOCP/Train/Get", {
        params: {
          TrainId: trainId,
          DatabaseId: databaseId,
        },
      });
      if (!response.data) {
        throw new Error("獲取列車列表失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取列車列表失敗:", error);
      throw error;
    }
  },

  // 創建列車
  create: async (params) => {
    try {
      if (!params.DatabaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!params.TrainName) {
        throw new Error("缺少列車名稱");
      }

      const response = await apiClient.post("/api/TOCP/Train/Create", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建列車失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建列車失敗:", error);
      throw error;
    }
  },

  // 更新列車
  update: async (params) => {
    try {
      if (!params.TrainId) {
        throw new Error("缺少列車ID");
      }

      const response = await apiClient.put("/api/TOCP/Train/Update", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新列車失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新列車失敗:", error);
      throw error;
    }
  },

  // 刪除列車
  delete: async (trainId) => {
    try {
      if (!trainId) {
        throw new Error("缺少列車ID");
      }

      const response = await apiClient.delete("/api/TOCP/Train/Delete", {
        data: {
          TrainId: trainId,
        },
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      return response.data;
    } catch (error) {
      console.error("刪除列車失敗:", error);
      throw error;
    }
  },

  // 匯出列車資料
  export: async (trainId) => {
    try {
      if (!trainId) {
        throw new Error("缺少列車ID");
      }

      const response = await apiClient.get("/api/TOCP/Train/Export", {
        params: {
          TrainId: trainId,
        },
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("匯出列車資料失敗:", error);
      throw error;
    }
  },

  // 匯入列車資料
  import: async (databaseId, importFile) => {
    try {
      if (!databaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!importFile) {
        throw new Error("缺少匯入檔案");
      }

      const formData = new FormData();
      formData.append("DatabaseId", databaseId);
      formData.append("ImportFile", importFile);

      const response = await apiClient.post("/api/TOCP/Train/Import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("匯入列車資料失敗:", error);
      throw error;
    }
  },
};

// 路線 API
export const route = {
  // 獲取路線列表
  get: async (routeId, databaseId) => {
    try {
      const response = await apiClient.get("/api/TOCP/Route/Get", {
        params: {
          RouteId: routeId,
          DatabaseId: databaseId,
        },
      });
      if (!response.data) {
        throw new Error("獲取路線列表失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取路線列表失敗:", error);
      throw error;
    }
  },

  // 獲取路線詳情
  getDetail: async (routeDetailId, routeId) => {
    try {
      const response = await apiClient.get("/api/TOCP/Route/GetDetail", {
        params: {
          RouteDetailId: routeDetailId,
          RouteId: routeId,
        },
      });
      if (!response.data) {
        throw new Error("獲取路線詳情失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取路線詳情失敗:", error);
      throw error;
    }
  },

  // 創建路線
  create: async (params) => {
    try {
      if (!params.DatabaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!params.RouteName) {
        throw new Error("缺少路線名稱");
      }

      const response = await apiClient.post("/api/TOCP/Route/Create", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建路線失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建路線失敗:", error);
      throw error;
    }
  },

  // 創建路線詳細資料
  createDetail: async (params) => {
    try {
      if (!params.RouteId) {
        throw new Error("缺少路線ID");
      }

      const response = await apiClient.post("/api/TOCP/Route/CreateDetail", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建路線詳細資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建路線詳細資料失敗:", error);
      throw error;
    }
  },

  // 更新路線
  update: async (params) => {
    try {
      if (!params.RouteId) {
        throw new Error("缺少路線ID");
      }

      const response = await apiClient.put("/api/TOCP/Route/Update", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新路線失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新路線失敗:", error);
      throw error;
    }
  },

  // 更新路線詳細資料
  updateDetail: async (params) => {
    try {
      if (!params.RouteId) {
        throw new Error("缺少路線ID");
      }
      if (!params.RouteDetailId) {
        throw new Error("缺少路線詳細資料ID");
      }

      const response = await apiClient.put("/api/TOCP/Route/UpdateDetail", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新路線詳細資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新路線詳細資料失敗:", error);
      throw error;
    }
  },

  // 刪除路線
  delete: async (routeId) => {
    try {
      if (!routeId) {
        throw new Error("缺少路線ID");
      }

      const response = await apiClient.delete("/api/TOCP/Route/Delete", {
        data: {
          RouteId: routeId,
        },
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      return response.data;
    } catch (error) {
      console.error("刪除路線失敗:", error);
      throw error;
    }
  },

  // 刪除路線詳細資料
  deleteDetail: async (params) => {
    try {
      if (!params.RouteId) {
        throw new Error("缺少路線ID");
      }
      if (!params.RouteDetailId) {
        throw new Error("缺少路線詳細資料ID");
      }

      const response = await apiClient.delete("/api/TOCP/Route/DeleteDetail", {
        data: params,
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      return response.data;
    } catch (error) {
      console.error("刪除路線詳細資料失敗:", error);
      throw error;
    }
  },

  // 重新排序路線詳細資料
  reorderDetail: async (params) => {
    try {
      if (!params.RouteDetailOrders || !Array.isArray(params.RouteDetailOrders)) {
        throw new Error("缺少路線詳細資料列表");
      }

      const response = await apiClient.post("/api/TOCP/Route/ReorderDetail", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "重新排序路線詳細資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("重新排序路線詳細資料失敗:", error);
      throw error;
    }
  },

  // 匯出路線資料
  export: async (routeId) => {
    try {
      if (!routeId) {
        throw new Error("缺少路線ID");
      }

      const response = await apiClient.get("/api/TOCP/Route/Export", {
        params: {
          RouteId: routeId,
        },
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("匯出路線資料失敗:", error);
      throw error;
    }
  },

  // 匯入路線資料
  import: async (databaseId, importFile) => {
    try {
      if (!databaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!importFile) {
        throw new Error("缺少匯入檔案");
      }

      const formData = new FormData();
      formData.append("DatabaseId", databaseId);
      formData.append("ImportFile", importFile);

      const response = await apiClient.post("/api/TOCP/Route/Import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("匯入路線資料失敗:", error);
      throw error;
    }
  },
};

// 速限 API (彎道)
export const speedLimitCurve = {
  // 獲取速限列表
  get: async (speedLimitId, databaseId) => {
    try {
      const response = await apiClient.get("/api/TOCP/SpeedLimitCurve/Get", {
        params: {
          SpeedLimitId: speedLimitId,
          DatabaseId: databaseId,
        },
      });
      if (!response.data) {
        throw new Error("獲取速限列表失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取速限列表失敗:", error);
      throw error;
    }
  },

  // 獲取速限詳細資料
  getDetail: async (speedLimitId) => {
    try {
      if (!speedLimitId) {
        throw new Error("缺少速限ID");
      }

      const response = await apiClient.get("/api/TOCP/SpeedLimitCurve/GetDetail", {
        params: {
          SpeedLimitId: speedLimitId,
        },
      });
      if (!response.data) {
        throw new Error("獲取速限詳細資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取速限詳細資料失敗:", error);
      throw error;
    }
  },

  // 創建速限
  create: async (params) => {
    try {
      if (!params.DatabaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!params.SpeedLimitName) {
        throw new Error("缺少速限名稱");
      }

      const response = await apiClient.post("/api/TOCP/SpeedLimitCurve/Create", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建速限失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建速限失敗:", error);
      throw error;
    }
  },

  // 創建速限詳細資料
  createDetail: async (params) => {
    try {
      if (!params.SpeedLimitId) {
        throw new Error("缺少速限ID");
      }

      const response = await apiClient.post("/api/TOCP/SpeedLimitCurve/CreateDetail", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建速限詳細資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建速限詳細資料失敗:", error);
      throw error;
    }
  },

  // 更新速限
  update: async (params) => {
    try {
      if (!params.SpeedLimitId) {
        throw new Error("缺少速限ID");
      }

      const response = await apiClient.put("/api/TOCP/SpeedLimitCurve/Update", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新速限失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新速限失敗:", error);
      throw error;
    }
  },

  // 更新速限詳細資料
  updateDetail: async (params) => {
    try {
      if (!params.SpeedLimitId) {
        throw new Error("缺少速限ID");
      }
      if (!params.SpeedLimitCurveDetailId) {
        throw new Error("缺少速限詳細資料ID");
      }

      const response = await apiClient.put("/api/TOCP/SpeedLimitCurve/UpdateDetail", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新速限詳細資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新速限詳細資料失敗:", error);
      throw error;
    }
  },

  // 刪除速限
  delete: async (speedLimitId) => {
    try {
      if (!speedLimitId) {
        throw new Error("缺少速限ID");
      }

      const response = await apiClient.delete("/api/TOCP/SpeedLimitCurve/Delete", {
        data: {
          SpeedLimitId: speedLimitId,
        },
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      return response.data;
    } catch (error) {
      console.error("刪除速限失敗:", error);
      throw error;
    }
  },

  // 刪除速限詳細資料
  deleteDetail: async (params) => {
    try {
      if (!params.SpeedLimitId) {
        throw new Error("缺少速限ID");
      }
      if (!params.SpeedLimitCurveDetailId) {
        throw new Error("缺少速限詳細資料ID");
      }

      const response = await apiClient.delete("/api/TOCP/SpeedLimitCurve/DeleteDetail", {
        data: params,
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      return response.data;
    } catch (error) {
      console.error("刪除速限詳細資料失敗:", error);
      throw error;
    }
  },

  // 重新排序速限詳細資料
  reorderDetail: async (params) => {
    try {
      if (!params.SpeedLimitCurveDetailOrders || !Array.isArray(params.SpeedLimitCurveDetailOrders)) {
        throw new Error("缺少速限詳細資料列表");
      }

      const response = await apiClient.post("/api/TOCP/SpeedLimitCurve/ReorderDetail", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "重新排序速限詳細資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("重新排序速限詳細資料失敗:", error);
      throw error;
    }
  },

  // 匯出速限資料
  export: async (speedLimitId) => {
    try {
      if (!speedLimitId) {
        throw new Error("缺少速限ID");
      }

      const response = await apiClient.get("/api/TOCP/SpeedLimitCurve/Export", {
        params: {
          SpeedLimitId: speedLimitId,
        },
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("匯出速限資料失敗:", error);
      throw error;
    }
  },

  // 匯入速限資料
  import: async (databaseId, importFile) => {
    try {
      if (!databaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!importFile) {
        throw new Error("缺少匯入檔案");
      }

      const formData = new FormData();
      formData.append("DatabaseId", databaseId);
      formData.append("ImportFile", importFile);

      const response = await apiClient.post("/api/TOCP/SpeedLimitCurve/Import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("匯入速限資料失敗:", error);
      throw error;
    }
  },
};

// 速限 API (下坡)
export const speedLimitDownhill = {
  // 獲取速限列表
  get: async (speedLimitId, databaseId) => {
    try {
      const response = await apiClient.get("/api/TOCP/SpeedLimitDownhill/Get", {
        params: {
          SpeedLimitId: speedLimitId,
          DatabaseId: databaseId,
        },
      });
      if (!response.data) {
        throw new Error("獲取速限列表失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取速限列表失敗:", error);
      throw error;
    }
  },

  // 獲取速限詳細資料
  getDetail: async (speedLimitId) => {
    try {
      if (!speedLimitId) {
        throw new Error("缺少速限ID");
      }

      const response = await apiClient.get("/api/TOCP/SpeedLimitDownhill/GetDetail", {
        params: {
          SpeedLimitId: speedLimitId,
        },
      });
      if (!response.data) {
        throw new Error("獲取速限詳細資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取速限詳細資料失敗:", error);
      throw error;
    }
  },

  // 創建速限
  create: async (params) => {
    try {
      if (!params.DatabaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!params.SpeedLimitName) {
        throw new Error("缺少速限名稱");
      }

      const response = await apiClient.post("/api/TOCP/SpeedLimitDownhill/Create", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建速限失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建速限失敗:", error);
      throw error;
    }
  },

  // 創建速限詳細資料
  createDetail: async (params) => {
    try {
      if (!params.SpeedLimitId) {
        throw new Error("缺少速限ID");
      }

      const response = await apiClient.post("/api/TOCP/SpeedLimitDownhill/CreateDetail", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建速限詳細資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建速限詳細資料失敗:", error);
      throw error;
    }
  },

  // 更新速限
  update: async (params) => {
    try {
      if (!params.SpeedLimitId) {
        throw new Error("缺少速限ID");
      }

      const response = await apiClient.put("/api/TOCP/SpeedLimitDownhill/Update", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新速限失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新速限失敗:", error);
      throw error;
    }
  },

  // 更新速限詳細資料
  updateDetail: async (params) => {
    try {
      if (!params.SpeedLimitId) {
        throw new Error("缺少速限ID");
      }
      if (!params.SpeedLimitDownhillDetailId) {
        throw new Error("缺少速限詳細資料ID");
      }

      const response = await apiClient.put("/api/TOCP/SpeedLimitDownhill/UpdateDetail", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新速限詳細資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新速限詳細資料失敗:", error);
      throw error;
    }
  },

  // 刪除速限
  delete: async (speedLimitId) => {
    try {
      if (!speedLimitId) {
        throw new Error("缺少速限ID");
      }

      const response = await apiClient.delete("/api/TOCP/SpeedLimitDownhill/Delete", {
        data: {
          SpeedLimitId: speedLimitId,
        },
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      return response.data;
    } catch (error) {
      console.error("刪除速限失敗:", error);
      throw error;
    }
  },

  // 刪除速限詳細資料
  deleteDetail: async (params) => {
    try {
      if (!params.SpeedLimitId) {
        throw new Error("缺少速限ID");
      }
      if (!params.SpeedLimitDownhillDetailId) {
        throw new Error("缺少速限詳細資料ID");
      }

      const response = await apiClient.delete("/api/TOCP/SpeedLimitDownhill/DeleteDetail", {
        data: params,
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      return response.data;
    } catch (error) {
      console.error("刪除速限詳細資料失敗:", error);
      throw error;
    }
  },

  // 重新排序速限詳細資料
  reorderDetail: async (params) => {
    try {
      if (!params.SpeedLimitDownhillDetailOrders || !Array.isArray(params.SpeedLimitDownhillDetailOrders)) {
        throw new Error("缺少速限詳細資料列表");
      }

      const response = await apiClient.post("/api/TOCP/SpeedLimitDownhill/ReorderDetail", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "重新排序速限詳細資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("重新排序速限詳細資料失敗:", error);
      throw error;
    }
  },

  // 匯出速限資料
  export: async (speedLimitId) => {
    try {
      if (!speedLimitId) {
        throw new Error("缺少速限ID");
      }

      const response = await apiClient.get("/api/TOCP/SpeedLimitDownhill/Export", {
        params: {
          SpeedLimitId: speedLimitId,
        },
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("匯出速限資料失敗:", error);
      throw error;
    }
  },

  // 匯入速限資料
  import: async (databaseId, importFile) => {
    try {
      if (!databaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!importFile) {
        throw new Error("缺少匯入檔案");
      }

      const formData = new FormData();
      formData.append("DatabaseId", databaseId);
      formData.append("ImportFile", importFile);

      const response = await apiClient.post("/api/TOCP/SpeedLimitDownhill/Import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("匯入速限資料失敗:", error);
      throw error;
    }
  },
};

// 動力車 API
export const vehiclePower = {
  // 獲取動力車列表
  get: async (vehiclePowerId, databaseId) => {
    try {
      const response = await apiClient.get("/api/TOCP/VehiclePower/Get", {
        params: {
          VehiclePowerId: vehiclePowerId,
          DatabaseId: databaseId,
        },
      });
      if (!response.data) {
        throw new Error("獲取動力車列表失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取動力車列表失敗:", error);
      throw error;
    }
  },

  create: async (params) => {
    try {
      if (!params.DatabaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!params.VehicleName) {
        throw new Error("缺少動力車名稱");
      }

      const response = await apiClient.post("/api/TOCP/VehiclePower/Create", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建動力車失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建動力車失敗:", error);
      throw error;
    }
  },

  // 更新動力車
  update: async (params) => {
    try {
      if (!params.VehicleId) {
        throw new Error("缺少動力車ID");
      }

      const response = await apiClient.put("/api/TOCP/VehiclePower/Update", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新動力車失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新動力車失敗:", error);
      throw error;
    }
  },

  // 刪除動力車
  delete: async (vehicleId) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少動力車ID");
      }

      const response = await apiClient.delete("/api/TOCP/VehiclePower/Delete", {
        data: {
          VehicleId: vehicleId,
        },
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "刪除動力車失敗");
      }
      return response.data;
    } catch (error) {
      console.error("刪除動力車失敗:", error);
      throw error;
    }
  },

  // 獲取車輛圖片
  getImage: async (vehicleId) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少車輛ID");
      }

      const response = await apiClient.get("/api/TOCP/VehiclePower/GetImage", {
        params: {
          VehicleId: vehicleId,
        },
        responseType: "blob", // 設置響應類型為 blob，用於處理圖片
      });

      // 如果響應成功但沒有圖片數據，返回 null
      if (response.data && response.data.size === 0) {
        return null;
      }

      // 如果有圖片數據，創建一個 URL 對象
      const imageUrl = URL.createObjectURL(response.data);
      return { imageUrl, blob: response.data };
    } catch (error) {
      console.error("獲取車輛圖片失敗:", error);
      throw error;
    }
  },

  // 上傳車輛圖片
  uploadImage: async (vehicleId, imageFile) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少車輛ID");
      }
      if (!imageFile) {
        throw new Error("缺少圖片檔案");
      }

      // 創建 FormData 對象
      const formData = new FormData();
      formData.append("VehicleId", vehicleId);
      formData.append("Image", imageFile);

      // 使用 multipart/form-data 格式上傳
      const response = await apiClient.post("/api/TOCP/VehiclePower/UploadImage", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "上傳車輛圖片失敗");
      }
      return response.data;
    } catch (error) {
      console.error("上傳車輛圖片失敗:", error);
      throw error;
    }
  },

  // 刪除車輛圖片
  deleteImage: async (vehicleId) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少車輛ID");
      }

      const response = await apiClient.delete("/api/TOCP/VehiclePower/DeleteImage", {
        data: {
          VehicleId: vehicleId,
        },
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });

      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "刪除車輛圖片失敗");
      }
      return response.data;
    } catch (error) {
      console.error("刪除車輛圖片失敗:", error);
      throw error;
    }
  },

  // 獲取牽引力資料
  getTractionData: async (vehicleId, tractionDataId) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少車輛ID");
      }

      const response = await apiClient.get("/api/TOCP/VehiclePower/GetTractionData", {
        params: {
          VehicleId: vehicleId,
          TractionDataId: tractionDataId,
        },
      });
      if (!response.data) {
        throw new Error("獲取牽引力資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取牽引力資料失敗:", error);
      throw error;
    }
  },

  // 創建牽引力資料
  createTractionData: async (params) => {
    try {
      if (!params.VehicleId) {
        throw new Error("缺少車輛ID");
      }

      const response = await apiClient.post("/api/TOCP/VehiclePower/CreateTractionData", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建牽引力資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建牽引力資料失敗:", error);
      throw error;
    }
  },

  // 更新牽引力資料
  updateTractionData: async (params) => {
    try {
      if (!params.VehicleId) {
        throw new Error("缺少車輛ID");
      }
      if (!params.TractionDataId) {
        throw new Error("缺少牽引力資料ID");
      }

      const response = await apiClient.put("/api/TOCP/VehiclePower/UpdateTractionData", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新牽引力資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新牽引力資料失敗:", error);
      throw error;
    }
  },

  // 重新排序牽引力資料
  reorderTractionData: async (params) => {
    try {
      if (!params.TractionDataOrders || !Array.isArray(params.TractionDataOrders)) {
        throw new Error("缺少牽引力資料列表");
      }

      const response = await apiClient.post("/api/TOCP/VehiclePower/ReorderTractionData", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "重新排序牽引力資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("重新排序牽引力資料失敗:", error);
      throw error;
    }
  },

  // 刪除牽引力資料
  deleteTractionData: async (params) => {
    try {
      if (!params.VehicleId) {
        throw new Error("缺少車輛ID");
      }
      if (!params.TractionDataId) {
        throw new Error("缺少牽引力資料ID");
      }

      const response = await apiClient.delete("/api/TOCP/VehiclePower/DeleteTractionData", {
        data: params,
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "刪除牽引力資料失敗");
      }
      return response.data;
    } catch (error) {
      console.error("刪除牽引力資料失敗:", error);
      throw error;
    }
  },

  // 獲取電力車能源消耗設定
  getEnergyConsumptionElectric: async (vehicleId) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少車輛ID");
      }

      const response = await apiClient.get("/api/TOCP/VehiclePower/GetEnergyConsumptionElectric", {
        params: {
          VehicleId: vehicleId,
        },
      });
      if (!response.data) {
        throw new Error("獲取電力車能源消耗設定失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取電力車能源消耗設定失敗:", error);
      throw error;
    }
  },

  // 更新電力車能源消耗設定
  updateEnergyConsumptionElectric: async (params) => {
    try {
      if (!params.VehicleId) {
        throw new Error("缺少車輛ID");
      }
      if (!params.EnergyConsumptionId) {
        throw new Error("缺少能源消耗設定ID");
      }

      const response = await apiClient.put("/api/TOCP/VehiclePower/UpdateEnergyConsumptionElectric", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新電力車能源消耗設定失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新電力車能源消耗設定失敗:", error);
      throw error;
    }
  },

  // 獲取電力車建表效率值
  getElectricEfficiencyValues: async (energyConsumptionId, electricEfficiencyValueId) => {
    try {
      const response = await apiClient.get("/api/TOCP/VehiclePower/GetElectricEfficiencyValues", {
        params: {
          EnergyConsumptionId: energyConsumptionId,
          ElectricEfficiencyValueId: electricEfficiencyValueId,
        },
      });
      if (!response.data) {
        throw new Error("獲取電力車建表效率值失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取電力車建表效率值失敗:", error);
      throw error;
    }
  },

  // 創建電力車建表效率值
  createElectricEfficiencyValue: async (params) => {
    try {
      if (!params.EnergyConsumptionId) {
        throw new Error("缺少能源消耗設定ID");
      }

      const response = await apiClient.post("/api/TOCP/VehiclePower/CreateElectricEfficiencyValue", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建電力車建表效率值失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建電力車建表效率值失敗:", error);
      throw error;
    }
  },

  // 更新電力車建表效率值
  updateElectricEfficiencyValue: async (params) => {
    try {
      if (!params.EnergyConsumptionId) {
        throw new Error("缺少能源消耗設定ID");
      }
      if (!params.ElectricEfficiencyValueId) {
        throw new Error("缺少效率值ID");
      }

      const response = await apiClient.put("/api/TOCP/VehiclePower/UpdateElectricEfficiencyValue", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新電力車建表效率值失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新電力車建表效率值失敗:", error);
      throw error;
    }
  },

  // 重新排序電力車建表效率值
  reorderElectricEfficiencyValue: async (params) => {
    try {
      if (!params.ElectricEfficiencyValueOrders || !Array.isArray(params.ElectricEfficiencyValueOrders)) {
        throw new Error("缺少電力車建表效率值列表");
      }

      const response = await apiClient.post("/api/TOCP/VehiclePower/ReorderElectricEfficiencyValue", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "重新排序電力車建表效率值失敗");
      }
      return response.data;
    } catch (error) {
      console.error("重新排序電力車建表效率值失敗:", error);
      throw error;
    }
  },

  // 刪除電力車建表效率值
  deleteElectricEfficiencyValue: async (params) => {
    try {
      if (!params.EnergyConsumptionId) {
        throw new Error("缺少能源消耗設定ID");
      }
      if (!params.ElectricEfficiencyValueId) {
        throw new Error("缺少效率值ID");
      }

      const response = await apiClient.delete("/api/TOCP/VehiclePower/DeleteElectricEfficiencyValue", {
        data: params,
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "刪除電力車建表效率值失敗");
      }
      return response.data;
    } catch (error) {
      console.error("刪除電力車建表效率值失敗:", error);
      throw error;
    }
  },

  // 獲取柴油車能源消耗設定
  getEnergyConsumptionDiesel: async (vehicleId) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少車輛ID");
      }

      const response = await apiClient.get("/api/TOCP/VehiclePower/GetEnergyConsumptionDiesel", {
        params: {
          VehicleId: vehicleId,
        },
      });
      if (!response.data) {
        throw new Error("獲取柴油車能源消耗設定失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取柴油車能源消耗設定失敗:", error);
      throw error;
    }
  },

  // 更新柴油車能源消耗設定
  updateEnergyConsumptionDiesel: async (params) => {
    try {
      if (!params.EnergyConsumptionId) {
        throw new Error("缺少能源消耗設定ID");
      }

      const response = await apiClient.put("/api/TOCP/VehiclePower/UpdateEnergyConsumptionDiesel", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新柴油車能源消耗設定失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新柴油車能源消耗設定失敗:", error);
      throw error;
    }
  },

  // 獲取柴油車用油量設定
  getDieselFuelConsumption: async (energyConsumptionId, dieselFuelConsumptionId) => {
    try {
      const response = await apiClient.get("/api/TOCP/VehiclePower/GetDieselFuelConsumption", {
        params: {
          EnergyConsumptionId: energyConsumptionId,
          DieselFuelConsumptionId: dieselFuelConsumptionId,
        },
      });
      if (!response.data) {
        throw new Error("獲取柴油車用油量設定失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取柴油車用油量設定失敗:", error);
      throw error;
    }
  },

  // 創建柴油車用油量設定
  createDieselFuelConsumption: async (params) => {
    try {
      if (!params.EnergyConsumptionId) {
        throw new Error("缺少能源消耗設定ID");
      }

      const response = await apiClient.post("/api/TOCP/VehiclePower/CreateDieselFuelConsumption", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建柴油車用油量設定失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建柴油車用油量設定失敗:", error);
      throw error;
    }
  },

  // 更新柴油車用油量設定
  updateDieselFuelConsumption: async (params) => {
    try {
      if (!params.EnergyConsumptionId) {
        throw new Error("缺少能源消耗設定ID");
      }
      if (!params.DieselFuelConsumptionId) {
        throw new Error("缺少用油量設定ID");
      }

      const response = await apiClient.put("/api/TOCP/VehiclePower/UpdateDieselFuelConsumption", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新柴油車用油量設定失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新柴油車用油量設定失敗:", error);
      throw error;
    }
  },

  // 重新排序柴油車用油量設定
  reorderDieselFuelConsumption: async (params) => {
    try {
      if (!params.DieselFuelConsumptionOrders || !Array.isArray(params.DieselFuelConsumptionOrders)) {
        throw new Error("缺少柴油車用油量設定列表");
      }

      const response = await apiClient.post("/api/TOCP/VehiclePower/ReorderDieselFuelConsumption", params, {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "重新排序柴油車用油量設定失敗");
      }
      return response.data;
    } catch (error) {
      console.error("重新排序柴油車用油量設定失敗:", error);
      throw error;
    }
  },

  // 刪除柴油車用油量設定
  deleteDieselFuelConsumption: async (params) => {
    try {
      if (!params.EnergyConsumptionId) {
        throw new Error("缺少能源消耗設定ID");
      }
      if (!params.DieselFuelConsumptionId) {
        throw new Error("缺少用油量設定ID");
      }

      const response = await apiClient.delete("/api/TOCP/VehiclePower/DeleteDieselFuelConsumption", {
        data: params,
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "刪除柴油車用油量設定失敗");
      }
      return response.data;
    } catch (error) {
      console.error("刪除柴油車用油量設定失敗:", error);
      throw error;
    }
  },

  // 匯出動力車資料
  export: async (vehicleId) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少動力車ID");
      }

      const response = await apiClient.get("/api/TOCP/VehiclePower/Export", {
        params: {
          VehicleId: vehicleId,
        },
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("匯出動力車資料失敗:", error);
      throw error;
    }
  },

  // 匯入動力車資料
  import: async (databaseId, importFile) => {
    try {
      if (!databaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!importFile) {
        throw new Error("缺少匯入檔案");
      }

      const formData = new FormData();
      formData.append("DatabaseId", databaseId);
      formData.append("ImportFile", importFile);

      const response = await apiClient.post("/api/TOCP/VehiclePower/Import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("匯入動力車資料失敗:", error);
      throw error;
    }
  },
};

// 非動力車 API
export const vehicleNonPower = {
  // 獲取非動力車列表
  get: async (vehicleNonPowerId, databaseId) => {
    try {
      const response = await apiClient.get("/api/TOCP/VehicleNonPower/Get", {
        params: {
          VehicleNonPowerId: vehicleNonPowerId,
          DatabaseId: databaseId,
        },
      });
      if (!response.data) {
        throw new Error("獲取非動力車列表失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取非動力車列表失敗:", error);
      throw error;
    }
  },

  create: async (params) => {
    try {
      if (!params.DatabaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!params.VehicleName) {
        throw new Error("缺少非動力車名稱");
      }

      const response = await apiClient.post("/api/TOCP/VehicleNonPower/Create", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建非動力車失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建非動力車失敗:", error);
      throw error;
    }
  },

  // 更新非動力車
  update: async (params) => {
    try {
      if (!params.VehicleId) {
        throw new Error("缺少非動力車ID");
      }

      const response = await apiClient.put("/api/TOCP/VehicleNonPower/Update", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新非動力車失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新非動力車失敗:", error);
      throw error;
    }
  },

  // 刪除非動力車
  delete: async (vehicleId) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少非動力車ID");
      }

      const response = await apiClient.delete("/api/TOCP/VehicleNonPower/Delete", {
        data: {
          VehicleId: vehicleId,
        },
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "刪除非動力車失敗");
      }
      return response.data;
    } catch (error) {
      console.error("刪除非動力車失敗:", error);
      throw error;
    }
  },

  // 獲取車輛圖片
  getImage: async (vehicleId) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少車輛ID");
      }

      const response = await apiClient.get("/api/TOCP/VehicleNonPower/GetImage", {
        params: {
          VehicleId: vehicleId,
        },
        responseType: "blob", // 設置響應類型為 blob，用於處理圖片
      });

      // 如果響應成功但沒有圖片數據，返回 null
      if (response.data && response.data.size === 0) {
        return null;
      }

      // 如果有圖片數據，創建一個 URL 對象
      const imageUrl = URL.createObjectURL(response.data);
      return { imageUrl, blob: response.data };
    } catch (error) {
      console.error("獲取車輛圖片失敗:", error);
      throw error;
    }
  },

  // 上傳車輛圖片
  uploadImage: async (vehicleId, imageFile) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少車輛ID");
      }
      if (!imageFile) {
        throw new Error("缺少圖片檔案");
      }

      // 創建 FormData 對象
      const formData = new FormData();
      formData.append("VehicleId", vehicleId);
      formData.append("Image", imageFile);

      // 使用 multipart/form-data 格式上傳
      const response = await apiClient.post("/api/TOCP/VehicleNonPower/UploadImage", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "上傳車輛圖片失敗");
      }
      return response.data;
    } catch (error) {
      console.error("上傳車輛圖片失敗:", error);
      throw error;
    }
  },

  // 刪除車輛圖片
  deleteImage: async (vehicleId) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少車輛ID");
      }

      const response = await apiClient.delete("/api/TOCP/VehicleNonPower/DeleteImage", {
        data: {
          VehicleId: vehicleId,
        },
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      });

      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "刪除車輛圖片失敗");
      }
      return response.data;
    } catch (error) {
      console.error("刪除車輛圖片失敗:", error);
      throw error;
    }
  },

  // 匯出非動力車資料
  export: async (vehicleId) => {
    try {
      if (!vehicleId) {
        throw new Error("缺少非動力車ID");
      }

      const response = await apiClient.get("/api/TOCP/VehicleNonPower/Export", {
        params: {
          VehicleId: vehicleId,
        },
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("匯出非動力車資料失敗:", error);
      throw error;
    }
  },

  // 匯入非動力車資料
  import: async (databaseId, importFile) => {
    try {
      if (!databaseId) {
        throw new Error("缺少資料庫ID");
      }
      if (!importFile) {
        throw new Error("缺少匯入檔案");
      }

      const formData = new FormData();
      formData.append("DatabaseId", databaseId);
      formData.append("ImportFile", importFile);

      const response = await apiClient.post("/api/TOCP/VehicleNonPower/Import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("匯入非動力車資料失敗:", error);
      throw error;
    }
  },
};

// 模擬案例 API
export const simulatorCase = {
  /**
   * Get a list of case templates (filtered by projectId if provided) or a specific case template.
   * GET /api/TOCP/SimulatorCase
   * GET /api/TOCP/SimulatorCase/{caseId}
   * @param {string|null} caseId - The ID of the case to retrieve. If null, fetches a list.
   * @param {string|null} projectId - The ID of the project to filter cases by (if caseId is null).
   * @returns {Promise<Array|Object>} A promise that resolves to an array of cases or a single case object.
   */
  get: (caseId, projectId) => {
    if (caseId) {
      return tocpRequest('get', `/api/TOCP/SimulatorCase/Get?CaseId=${caseId}`);
    }
    const params = projectId ? { ProjectId: projectId } : {}; // Use ProjectId for query param
    return tocpRequest('get', '/api/TOCP/SimulatorCase/Get', null, params);
  },

  /**
   * Create a new case template.
   * POST /api/TOCP/SimulatorCase
   * @param {Object} caseCreateRequest - The case creation request payload.
   * Expected structure: { ProjectId: string, Definition: { CaseName: string, ..., MainDatabaseId: number, TrainFormationId: number, RouteId: number, ... } }
   * @returns {Promise<Object>} A promise that resolves to the created case object.
   */
  create: (caseCreateRequest) => {
    return tocpRequest('post', '/api/TOCP/SimulatorCase/Create', caseCreateRequest);
  },

  /**
   * Run a simulation based on a case template.
   * POST /api/TOCP/SimulatorCase/Simulate
   * @param {Object} simulateRequest - The simulation request payload, typically { CaseId: string, ... }.
   * @returns {Promise<Object>} A promise that resolves to the simulation initiation response.
   */
  simulate: (simulateRequest) => {
    return tocpRequest('post', '/api/TOCP/SimulatorCase/Simulate', simulateRequest);
  },

  /**
   * Get the results of a specific simulation run.
   * GET /api/TOCP/SimulatorCase/GetResult/{caseId} or /api/TOCP/SimulatorCase/Result/{caseId} - Check OpenAPI.yaml
   * @param {string} caseId - The ID of the case for which to retrieve results.
   * @returns {Promise<Object>} A promise that resolves to the simulation results.
   */
  getResult: (caseId) => {
    // Assuming caseId is part of the path, adjust if it's a query param.
    // Also, confirm if the path is GetResult/{id} or Result/{id}
    return tocpRequest('get', `/api/TOCP/SimulatorCase/GetResult/${caseId}`);
  },

  /**
   * Get the progress of an ongoing simulation.
   * GET /api/TOCP/SimulatorCase/GetProgress/{processId} or /api/TOCP/SimulatorCase/Progress/{processId} - Check OpenAPI.yaml
   * @param {string} processId - The ID of the simulation process.
   * @returns {Promise<Object>} A promise that resolves to the simulation progress.
   */
  getProgress: (processId) => {
    // Assuming processId is part of the path, adjust if it's a query param.
    // Also, confirm if the path is GetProgress/{id} or Progress/{id}
    return tocpRequest('get', `/api/TOCP/SimulatorCase/GetProgress/${processId}`);
  },

  /**
   * Get a list of simulated cases (results) available for analysis.
   * GET /api/TOCP/SimulatorCase/IndexResult
   * @param {string|null} projectId - The ID of the project to filter results by.
   * @returns {Promise<Array>} A promise that resolves to an array of simulation result summaries.
   */
  indexResults: (projectId, limit, page) => {
    const params = projectId ? { ProjectId: projectId, Limit: limit, Page: page } : {}; // Use ProjectId for query param
    return tocpRequest('get', '/api/TOCP/SimulatorCase/IndexResult', null, params);
  },

  update: (updateRequest) => {
    return tocpRequest('put', `/api/TOCP/SimulatorCase/Update`, updateRequest);
  },

  /**
   * Delete a simulator case
   * DELETE /api/TOCP/SimulatorCase/Delete
   * @param {Object} deleteRequest - The delete request payload, typically { CaseId: string }.
   * @returns {Promise<Object>} A promise that resolves to the deletion response.
   */
  delete: (deleteRequest) => {
    return tocpRequest('delete', '/api/TOCP/SimulatorCase/Delete', deleteRequest);
  },
};

// 模擬專案 API
export const simulatorProject = {
  // 獲取模擬專案列表
  get: async (projectId, accountId) => {
    try {
      const response = await apiClient.get("/api/TOCP/SimulatorProject/Get", {
      params: {
          ProjectId: projectId,
          AccountId: accountId,
        },
      });
      if (!response.data) {
        throw new Error("獲取模擬專案列表失敗");
      }
      // 假設回傳結構為 { Projects: [...] }
      return response.data;
    } catch (error) {
      console.error("獲取模擬專案列表失敗:", error);
      throw error;
    }
  },

  // 創建模擬專案
  create: async (params) => {
    // 參數應為 SimulatorProjectCreateRequest 結構
    try {
      // 根據 SimulatorProjectCreateRequest 檢查必要參數
      // 例如: if (!params.ProjectName) throw new Error("缺少專案名稱");
      // 添加其他必要的參數檢查...

      const response = await apiClient.post("/api/TOCP/SimulatorProject/Create", params, {
        headers: {
          "Content-Type": "application/json-patch+json", // 或 application/json
        },
      });
      // 假設回傳結構為 { Success: bool, Result: Project }
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建模擬專案失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建模擬專案失敗:", error);
      throw error;
    }
  },

  // 更新模擬專案
  update: async (params) => {
    // 參數應為 SimulatorProjectUpdateRequest 結構
    try {
      // 根據 SimulatorProjectUpdateRequest 檢查必要參數
      if (!params.ProjectId) {
        throw new Error("缺少專案ID");
      }
      // 添加其他必要的參數檢查...

      const response = await apiClient.put("/api/TOCP/SimulatorProject/Update", params);
      // 假設回傳結構為 { Success: bool, Result: Project }
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新模擬專案失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新模擬專案失敗:", error);
      throw error;
    }
  },

  // 刪除模擬專案
  delete: async (params) => {
    // 參數應為 SimulatorProjectDeleteRequest 結構
    try {
      // 根據 SimulatorProjectDeleteRequest 檢查必要參數
      if (!params.ProjectId) {
        throw new Error("缺少專案ID");
      }
      // 添加其他必要的參數檢查...

      const response = await apiClient.delete("/api/TOCP/SimulatorProject/Delete", {
        data: params,
        headers: {
          "Content-Type": "application/json-patch+json", // 或 application/json
        },
      });
      // 假設成功時回傳 EmptyResponse 或特定成功標誌
      return response.data;
    } catch (error) {
      console.error("刪除模擬專案失敗:", error);
      throw error;
    }
  },
};

// 模擬功能 (目前為模擬數據用，實際需要後端API支持)
// 註: 下方的 simulation 物件將被新的 API 取代，暫時保留作為參考或過渡
// export const simulation = { ... };

// 模擬數據 (測試用)
// const mockData = { ... };
